/**
 * Label text resolution.
 *
 * A label is either a single field or a template like `{pipe_id} - {status}`.
 * Templates are resolved by SUBSTITUTION ONLY — there is no evaluator, no
 * `Function`, no `eval`. A token that names no field resolves to an empty
 * string rather than leaking the raw token onto the map.
 */

import type { LabelClass, LabelFilter } from './LabelTypes';

/** `{field_name}` — letters, digits, underscore, dot, colon and hyphen. */
const TOKEN = /\{([A-Za-z0-9_.:-]+)\}/g;

/** Field names referenced by a template, in order of first appearance. */
export function extractExpressionFields(expression: string): string[] {
  const fields: string[] = [];
  for (const match of expression.matchAll(TOKEN)) {
    if (!fields.includes(match[1])) fields.push(match[1]);
  }
  return fields;
}

/**
 * Reads a property tolerantly.
 *
 * Services are inconsistent about field-name casing between their metadata and
 * their GeoJSON output (the same mismatch the symbology renderer handles), so
 * an exact miss falls back to a case-insensitive match.
 */
function readProperty(properties: Record<string, unknown>, field: string): unknown {
  if (field in properties) return properties[field];
  const key = Object.keys(properties).find(
    (candidate) => candidate.toLowerCase() === field.toLowerCase(),
  );
  return key === undefined ? undefined : properties[key];
}

function asText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return '';
  return String(value);
}

/** The text to draw for one feature, or '' when there is nothing to show. */
export function resolveLabelText(
  labelClass: Pick<LabelClass, 'field' | 'expression'>,
  properties: Record<string, unknown> | null | undefined,
): string {
  const source = properties ?? {};
  const expression = labelClass.expression.trim();

  if (expression) {
    const resolved = expression.replace(TOKEN, (_whole, field: string) =>
      asText(readProperty(source, field)),
    );
    return resolved.trim();
  }

  const field = labelClass.field.trim();
  if (!field) return '';
  return asText(readProperty(source, field)).trim();
}

/** Every field the class needs, so a query can request just those. */
export function fieldsUsedBy(labelClass: LabelClass): string[] {
  const fields = labelClass.expression.trim()
    ? extractExpressionFields(labelClass.expression)
    : labelClass.field.trim()
      ? [labelClass.field.trim()]
      : [];
  if (labelClass.filter.enabled && labelClass.filter.field) fields.push(labelClass.filter.field);
  return Array.from(new Set(fields));
}

/* ─────────────────────────────── Filtering ──────────────────────────────── */

/** Applies a structured label filter to one feature's properties. */
export function passesLabelFilter(
  filter: LabelFilter,
  properties: Record<string, unknown> | null | undefined,
): boolean {
  if (!filter.enabled || !filter.field.trim()) return true;

  const raw = readProperty(properties ?? {}, filter.field.trim());
  const actual = asText(raw).trim();
  const expected = filter.value.trim();

  switch (filter.operator) {
    case '=':
      return actual.toLowerCase() === expected.toLowerCase();
    case '!=':
      return actual.toLowerCase() !== expected.toLowerCase();
    case 'contains':
      return actual.toLowerCase().includes(expected.toLowerCase());
    case '>':
    case '<': {
      // Compare as numbers when both sides are numeric, otherwise lexically —
      // a text status field still sorts sensibly instead of silently failing.
      const left = Number(actual);
      const right = Number(expected);
      if (Number.isFinite(left) && Number.isFinite(right)) {
        return filter.operator === '>' ? left > right : left < right;
      }
      return filter.operator === '>' ? actual > expected : actual < expected;
    }
    default:
      return true;
  }
}
